import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GeneralResponse } from 'app/models/general-response.model';
import { OrdersStore } from 'app/modules/raw-data/orders/orders.store';
import { environment } from 'environments/environment';
import { BehaviorSubject, interval, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

/**
 * Service functionality for the bell button displayed on the top bar of the application.
 *
 * This service is responsible for providing live updates related to the number of orders
 * that are in specific states. The primary functionality includes:
 *
 * 1. **Pending Orders Count**: It retrieves and displays the count of orders that are currently in
 * the PENDING state. These are orders that are awaiting action or processing.
 *
 * 2. **Discrepancies Count**: It also tracks the number of orders that are flagged with DISCREPANCIES.
 * These orders require attention due to issues like mismatched data or other anomalies.
 *
 * Use Cases:
 * - **Pending Orders**: To keep track of orders that are waiting to be processed or require attention.
 * - **Discrepancies**: To highlight orders that have issues or errors, which need resolution before proceeding.
 *
 * The bell icon dynamically updates based on the count of pending orders and discrepancies,
 * ensuring the user interface reflects the current operational status of the orders.
 */

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    private _pendingCountSubject = new BehaviorSubject<number>(0); // Holds the current notification count of the PENDING orders
    private _discrepancyCountSubject = new BehaviorSubject<number>(0); // Holds the current notification count
    private _completeCountSubject = new BehaviorSubject<number>(0); // Holds the current notification count
    pendingCount$ = this._pendingCountSubject.asObservable(); // Expose the observable
    discrepancyCount$ = this._discrepancyCountSubject.asObservable(); // Expose the observable
    completeCount$ = this._completeCountSubject.asObservable(); // Expose the observable

    private previousPendingCount = 0;
    private previousDiscrepancyCount = 0;
    private previousCompleteCount = 0;
    // Integer 2 represents a pending Order
    private pendingStatus = 2;
    private completeStatus = 7;

    // Integer 8 represents a discrepancy
    private discrepancyStatus = 8;

    /**
     * Constructor
     * @param _http
     */
    constructor(private _http: HttpClient, private _ordersStore: OrdersStore) {
        // Initialize by immediately fetching the notification count on service instantiation
        this.getNotificationCount().subscribe();
    }

    // Simulate a backend API call that returns the length as an Observable
    getNotificationCount(): Observable<number> {
        return this._http
            .get<GeneralResponse>(
                `${environment.backendURL}${environment.ordersSummaryURL}/${this.pendingStatus},${this.discrepancyStatus},${this.completeStatus}`
            )
            .pipe(
                map((response: GeneralResponse) => {
                    /**
          * The structure of the workload data returned by the service.
          * 
          * The workload data is represented as a list of arrays, where each array contains two elements:
          * 
          * 1. **Status Code**: An integer representing the order status (e.g., PENDING, DISCREPANCY, etc.).
          * 2. **Count**: A long integer representing the number of orders in that particular status.
          * 
          * Example of the returned structure:
          * 
          * [
          *     {
          *         "status": 2,
          *         "count": 2   // 2 orders with status code 2 (e.g., PENDING)
          *     },
          *     {
          *         "status": 8,
          *         "count": 1   // 1 order with status code 8 (e.g., DISCREPANCY)
          *     }
          * ]

          */
                    const result = response.workload ? response.workload : [];
                    let pendingCount,
                        completeCount,
                        discrepancyCount = 0;
                    result.forEach((item) => {
                        if (item.status === 2) {
                            pendingCount = item.count; // Assign count if status is 7
                        } else if (item.status === 8) {
                            discrepancyCount = item.count;
                        } else if (item.status === 7) {
                            completeCount = item.count;
                        }
                    });
                    //pendingCount = result[0] ? result[0].count : 0; // First value is the PENDING order
                    //discrepancyCount = result[1] ? result[1].count : 0;
                    this._pendingCountSubject.next(pendingCount);
                    this._discrepancyCountSubject.next(discrepancyCount); // Update the BehaviorSubject
                    this._completeCountSubject.next(completeCount);
                    if (
                        this.previousPendingCount != pendingCount ||
                        this.previousDiscrepancyCount != discrepancyCount ||
                        this.previousCompleteCount != completeCount
                    ) {
                        this._ordersStore.getFilters().subscribe();
                    }
                    this.previousPendingCount = pendingCount;
                    this.previousCompleteCount = completeCount;
                    this.previousDiscrepancyCount = discrepancyCount;
                    return pendingCount; // Return the count
                }),

                // Handle errors and return an observable with a fallback value of 0
                catchError((err) => {
                    console.error(err);
                    this._pendingCountSubject.next(0); // In case of error, set count to 0
                    this._discrepancyCountSubject.next(0); // In case of error, set count to 0
                    return of(0); // Return an observable of 0 in case of error
                }),

                // Cache the result and share with multiple subscribers
                shareReplay(1) // Cache the result for sharing with multiple subscribers
            );
    }

    startPolling(): Observable<number> {
        return interval(10000).pipe(
            // Poll every 10 seconds
            switchMap(() => this.getNotificationCount()) // Call getNotificationCount on each interval
        );
    }
}
